import { createDraftStore } from '../../shared/draft/wizardDraftStore';
import { describeAge, type DraftDescription } from '../../shared/draft/draftDescription';

/**
 * The survey builder's draft (`/surveys/new`).
 *
 * Every field was plain useState and nothing was written to any storage, so a reload — or
 * a mis-clicked browser Back, which leaves for /surveys — threw away the whole survey:
 * name, title, description, instructions, the pasted questions and the group count
 * (#403). A researcher who pastes thirty questions and mis-clicks loses all of it.
 *
 * localStorage for the same reason as the /simulate draft: nothing exists server-side
 * until the survey is created, so there is no entity to re-attach to, and the case people
 * report is an accidental close rather than an in-tab navigation.
 */
export interface SurveyBuilderDraft {
  surveyName: string;
  surveyTitle: string;
  surveyDescription: string;
  surveyInstructions: string;
  questions: string;
  goal: string;
  context: string;
  parsedQuestions: unknown[];
  /**
   * The block definitions the preview owns (labels, randomize_within, display_condition,
   * rapid_fire). Persisted because `parsedQuestions` carry a `block_id` that points AT
   * them: restoring the questions without these left every block header rendering the raw
   * `block_1760…` id, the question's own block missing from its dropdown, a label editor
   * that silently did nothing, and Create writing questions with a dangling `block_id`
   * against `blocks: []`.
   */
  blocks?: unknown[];
  /**
   * The rest of the preview-owned settings, for the same reason as `blocks`: a restore that
   * brings the questions back but not these silently resets them to defaults — and
   * `surveyLevelProbing` defaults to ON, so a researcher who turned it off got it back on
   * without being told. Every one of these has an `initial*` prop on the preview, so they
   * round-trip; only `enableAiRedundancySkip` has none and is therefore not persisted.
   *
   * All OPTIONAL, and not for convenience: a draft written before this change is already
   * sitting in users' localStorage for up to 7 days with none of these keys, so `load()`
   * genuinely can return a draft without them. The restore defaults each one explicitly.
   */
  maxResponses?: number | null;
  redirectConfig?: unknown | null;
  adaptiveProbing?: boolean;
  surveyLevelProbing?: boolean;
  minLinkedInConnections?: number | null;
  /** Survey-wide countdown (spec 35). Stored as the API shape so restore needs no conversion. */
  timerConfig?: unknown | null;
  groupCount: number;
  /** 'creating' is deliberately never persisted — see draftStepFor below. */
  step: 'input' | 'preview';
}

const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

export const surveyBuilderDraftStore = createDraftStore<SurveyBuilderDraft>({
  namespace: 'bp.surveyBuilder.draft.v1',
  storage: 'local',
  maxAgeMs: MAX_AGE_MS,
});

/**
 * A survey mid-creation must never be restored INTO the creating state: the request
 * either completed (so the draft is stale) or failed (so the user needs the form back,
 * not a spinner). Either way 'input' is the honest landing place.
 */
export function draftStepFor(step: string): 'input' | 'preview' {
  return step === 'preview' ? 'preview' : 'input';
}

/** Anything the user actually typed. Opening the page and leaving is not worth restoring. */
export function surveyDraftIsWorthRestoring(
  draft: SurveyBuilderDraft | null,
): draft is SurveyBuilderDraft {
  if (!draft) return false;
  return Boolean(
    draft.surveyName?.trim() ||
      draft.surveyTitle?.trim() ||
      draft.surveyDescription?.trim() ||
      draft.surveyInstructions?.trim() ||
      draft.questions?.trim() ||
      draft.goal?.trim() ||
      draft.context?.trim() ||
      draft.parsedQuestions?.length,
  );
}

/** What the restore banner says — built from the draft, never a generic message. */
export function describeSurveyDraft(
  draft: SurveyBuilderDraft & { updatedAt: number },
  now: number,
): DraftDescription {
  const restored: string[] = [];
  if (draft.surveyName?.trim()) restored.push('survey name');
  if (draft.surveyTitle?.trim()) restored.push('title');
  if (draft.surveyDescription?.trim()) restored.push('description');
  if (draft.surveyInstructions?.trim()) restored.push('instructions');

  // Prefer the parsed count; fall back to the raw paste. Saying "0 questions" when the
  // paste survived but the parse had not finished would read as data loss.
  const parsed = draft.parsedQuestions?.length || 0;
  if (parsed) restored.push(`${parsed} question${parsed === 1 ? '' : 's'}`);
  else if (draft.questions?.trim()) restored.push('your pasted questions');

  if (draft.groupCount > 1) restored.push(`${draft.groupCount} groups`);

  return {
    age: describeAge(now - draft.updatedAt),
    restored,
    imagesNeedReupload: false, // the builder has no image uploads
  };
}
